import React, { ChangeEvent, forwardRef, Fragment, PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Select as SelectStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { mockSelectChangeEvent } from '../../lib/utils'
import useControlled from '../../lib/hooks/useControlled'
import useMediaQuery from '../../lib/hooks/useMediaQuery'
import { CommonProps } from '../helperTypes'
import ComboBox, { ComboBoxProps, ComboBoxGroupedOption } from '../ComboBox'

export interface SelectOption {
  label: string
  value: string
  disabled?: boolean
  hidden?: boolean
}

type Props = PropsWithChildren<{
  /** optional props to pass to the custom ComboBox component */
  cbProps?: ComboBoxProps<SelectOption>
  /** The default value to be used for an uncontrolled component */
  defaultValue?: number | string
  /**
   * If true, input will be disabled
   * @default false
   */
  disabled?: boolean
  /**
   * Use this method to define group of options.<br />
   * The string returned for each option will determine the group names.
   * @returns group name
   */
  groupBy?: (option: SelectOption) => string
  /** html id attribute passed to the native input */
  id?: string
  /** If true, the input will indicate an error */
  invalid?: boolean
  /**
   * Choose mode to switch from native html to full custom or automatic fallback
   * @default AUTO
   */
  mode?: 'AUTO' | 'CUSTOM' | 'NATIVE'
  /** The callback for when the input checked status changes */
  onChange?: (event: ChangeEvent<HTMLSelectElement>, value: string) => void
  /** List of options */
  options?: SelectOption[]
  /** The text to display when no option has been selected */
  placeholder?: string
  /**
   * The size of the input
   * @default medium
   */
  size?: 'small' | 'medium'
  /** selected option */
  value?: number | string
  /** If true, the input will indicate a success */
  valid?: boolean
}>

export type SelectProps = Props & CommonProps
const useStyles = createUseStyles(SelectStyles, 'select')

/**
 * mock onChange event when using ComboBox internally
 * @param cbValue value transmitted by ComboBox component
 * @returns mocked onChange event
 */
const mockComboBoxChangeEvent = (cbValue: any, id?: string) => {
  const getCBValue = (value: any) => {
    if (typeof value === 'string') return value
    if (value === undefined) return ''
    if ((value as any).value) return (value as any).value
    return (value as any)[Object.keys(value as Object)[0]]
  }
  return mockSelectChangeEvent(getCBValue(cbValue), id)
}

export const Select = (props: SelectProps, ref: React.Ref<HTMLSelectElement>) => {
  const {
    'aria-label': ariaLabel,
    children,
    className,
    cbProps,
    defaultValue,
    disabled,
    groupBy,
    id,
    invalid,
    mode = 'AUTO',
    onChange,
    options,
    placeholder,
    size,
    valid,
    value: valueProp,
    ...rest
  } = props
  const classes = useStyles()
  const [value, setValueState] = useControlled({
    value: valueProp,
    defaultValue,
  })
  const handleChange = React.useCallback(
    (val: any, evt?: ChangeEvent<HTMLSelectElement>) => {
      setValueState(val)
      // ComboBox does not transmit native event
      // and ComboBox value may be an object of any type
      const event = evt || mockComboBoxChangeEvent(val, id)
      onChange && onChange(event, val)
    },
    [id, onChange, setValueState],
  )

  // compute grouped options with groupBy
  const groupedOptions = React.useMemo(() => {
    if (!groupBy || !options) return []

    const result: ComboBoxGroupedOption<SelectOption>[] = []
    const indexByGroup = new Map()
    options.forEach((option: SelectOption) => {
      const groupName = groupBy(option)
      const groupIndex = indexByGroup.get(groupName)
      if (groupIndex >= 0) {
        result[groupIndex].options.push(option)
      } else {
        indexByGroup.set(groupName, result.length)
        result.push({ group: groupName, options: [option] })
      }
    })
    return result
  }, [groupBy, options])

  // custom html select if browser supports it and depending on "mode"
  const matches = useMediaQuery('(hover: hover)')
  if (options && (mode === 'CUSTOM' || (mode === 'AUTO' && matches))) {
    const finalCbProps: ComboBoxProps<SelectOption> = {
      ...cbProps,
      'aria-label': ariaLabel,
      id,
      multiple: false,
      value,
      defaultValue: defaultValue as string,
      onChange: (opt: SelectOption) => handleChange(opt.value),
    }
    return (
      <ComboBox
        className={className}
        disabled={disabled}
        groupBy={groupBy}
        options={options}
        valid={valid}
        invalid={invalid}
        placeholder={placeholder}
        readOnly
        size={size}
        {...finalCbProps}
      />
    )
  }

  return (
    <select
      aria-label={ariaLabel}
      className={clsx(
        className,
        classes.root,
        size && classes[`_${size}`],
        valid && classes._valid,
        invalid && classes._invalid,
        placeholder && value === undefined && classes._hasPlaceholder,
      )}
      id={id}
      disabled={disabled}
      ref={ref}
      value={value || ''}
      onChange={(evt) => handleChange(evt.target.value, evt)}
      {...rest}
    >
      {placeholder && (
        <option value='' disabled hidden>
          {placeholder}
        </option>
      )}
      {children}
      {groupBy &&
        options &&
        groupedOptions.map((item: ComboBoxGroupedOption<SelectOption>, index: number) => (
          <Fragment key={`group-${item.group}${index}`}>
            <optgroup label={item.group}>
              {item.options.map((option: SelectOption, key: number) => (
                <option key={key} value={option.value} disabled={option.disabled} hidden={option.hidden}>
                  {option.label}
                </option>
              ))}
            </optgroup>
          </Fragment>
        ))}
      {!groupBy &&
        options &&
        options.map((option: SelectOption, key: number) => (
          <option key={key} value={option.value} disabled={option.disabled} hidden={option.hidden}>
            {option.label}
          </option>
        ))}
    </select>
  )
}

export default forwardRef<HTMLSelectElement, SelectProps>(Select)
